import React, { useState } from 'react';
import {
  Button,
  Tooltip,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
} from '@mui/material';
import {
  PhotoCamera as TryOnIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../../contexts/AuthContext";
import TryOnService from '../../services/try-on-service';
import { useToast } from '../../contexts/ToastContext';

interface TryOnButtonProps {
  outfitId: string;
  outfitImageUrl: string;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  onTryOnStart?: () => void;
  onTryOnComplete?: (resultImageUrl: string) => void;
  onTryOnError?: (error: string) => void;
  onOpenModal?: (sessionId: string) => void;
}

const TryOnButton: React.FC<TryOnButtonProps> = ({
  outfitId,
  outfitImageUrl,
  disabled = false,
  size = 'small',
  onTryOnStart,
  onTryOnComplete,
  onTryOnError,
  onOpenModal,
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [hasProfilePicture, setHasProfilePicture] = useState<boolean | null>(null);
  const [showProfilePicturePrompt, setShowProfilePicturePrompt] = useState(false);

  // Check if user has profile picture on first render
  // Use a ref to track if we've already checked to prevent repeated API calls
  const hasCheckedRef = React.useRef(false);
  
  React.useEffect(() => {
    const checkProfilePicture = async () => {
      if (!user || hasCheckedRef.current) return;
      
      hasCheckedRef.current = true;
      
      try {
        const hasPicture = await TryOnService.hasProfilePicture();
        setHasProfilePicture(hasPicture);
      } catch (error) {
        console.error('Failed to check profile picture status:', error);
        setHasProfilePicture(false);
      }
    };

    checkProfilePicture();
  }, [user]);

  const handleTryOnClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      showToast('Please sign in to try on outfits', 'warning');
      return;
    }

    if (hasProfilePicture === false) {
      setShowProfilePicturePrompt(true);
      return;
    }

    if (isLoading) return;

    setIsLoading(true);
    onTryOnStart?.();

    try {
      const response = await TryOnService.generateTryOn(outfitId);
      
      // Open the try-on modal with the session ID
      if (onOpenModal) {
        onOpenModal(response.sessionId);
      } else {
        // If no modal handler provided, poll for completion
        const result = await TryOnService.pollTryOnStatus(response.sessionId);
        
        if (result.status === 'completed' && result.resultImageUrl) {
          onTryOnComplete?.(result.resultImageUrl);
          showToast('Try-on completed successfully!', 'success');
        } else if (result.status === 'failed') {
          const errorMessage = result.errorMessage || 'Try-on failed';
          onTryOnError?.(errorMessage);
          showToast(errorMessage, 'error');
        }
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to start try-on';
      onTryOnError?.(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseProfilePicturePrompt = () => {
    setShowProfilePicturePrompt(false);
  };

  const handleGoToSettings = () => {
    setShowProfilePicturePrompt(false);
    navigate('/settings');
  };

  const getButtonContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CircularProgress size={16} color="inherit" />
          <span>Try-On</span>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {hasProfilePicture === false ? (
          <PersonIcon sx={{ fontSize: size === 'small' ? 16 : 20 }} />
        ) : (
          <TryOnIcon sx={{ fontSize: size === 'small' ? 16 : 20 }} />
        )}
        <span>Try-On</span>
      </Box>
    );
  };

  const getTooltipTitle = () => {
    if (!user) return 'Sign in to try on outfits';
    if (hasProfilePicture === false) return 'Upload a profile picture to use try-on';
    if (isLoading) return 'Processing try-on...';
    return 'Try on this outfit virtually';
  };

  const isButtonDisabled = disabled || isLoading || !user || hasProfilePicture === false;

  return (
    <>
      <Tooltip title={getTooltipTitle()}>
        <span>
          <Button
            variant="outlined"
            size={size}
            onClick={handleTryOnClick}
            disabled={isButtonDisabled}
            sx={{
              minWidth: size === 'small' ? 'auto' : undefined,
              px: size === 'small' ? 1 : 2,
              py: size === 'small' ? 0.5 : 1,
              fontSize: size === 'small' ? '0.75rem' : undefined,
              borderColor: hasProfilePicture === false ? 'warning.main' : 'primary.main',
              color: hasProfilePicture === false ? 'warning.main' : 'primary.main',
              '&:hover': {
                borderColor: hasProfilePicture === false ? 'warning.dark' : 'primary.dark',
                backgroundColor: hasProfilePicture === false 
                  ? 'rgba(237, 108, 2, 0.04)' 
                  : 'rgba(25, 118, 210, 0.04)',
              },
              '&:disabled': {
                borderColor: 'action.disabled',
                color: 'action.disabled',
              },
            }}
          >
            {getButtonContent()}
          </Button>
        </span>
      </Tooltip>

      {/* Profile Picture Required Dialog */}
      <Dialog
        open={showProfilePicturePrompt}
        onClose={handleCloseProfilePicturePrompt}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon color="warning" />
            <Typography variant="h6">Profile Picture Required</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            To use the virtual try-on feature, you need to upload a profile picture first.
          </Alert>
          <Typography variant="body1" paragraph>
            The try-on feature uses your profile picture to show how outfits look on you. 
            Your profile picture is kept private and only used for try-on purposes.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can upload a profile picture in your settings and start trying on outfits right away!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProfilePicturePrompt} color="secondary">
            Maybe Later
          </Button>
          <Button
            onClick={handleGoToSettings}
            variant="contained"
            startIcon={<SettingsIcon />}
            color="primary"
          >
            Go to Settings
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TryOnButton;